import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { notify } from "utils/helperFuncs";
import { GET_BANNER_DETAIL, GET_BANNER_QUEUE, SEARCH_BANNERS } from "graphql/banner/query";
import { CREATE_BANNER, SAVE_BANNER_QUEUE, SWITCH_BANNER_STATUS, UPDATE_BANNER } from "graphql/banner/mutation";
import { mapSearchFiltersToBackend } from "./apiAdapters";

export const DEFAULT_PAGINATION = { offset: 0, limit: 10 };

/**
 * Hook tìm kiếm danh sách banner / popup / announcement bar
 */
export function useSearchBanners(filters = {}, pagination = DEFAULT_PAGINATION) {
  const backendFilters = useMemo(() => mapSearchFiltersToBackend(filters), [filters]);

  const { data, loading, error, refetch } = useQuery(SEARCH_BANNERS, {
    variables: {
      filters: backendFilters,
      pagination: {
        offset: pagination.offset,
        limit: pagination.limit,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
  });

  const searchResult = data?.banner?.searchBanners;
  const banners = searchResult?.banners || [];
  const paginationData = searchResult?.paginationData || {
    total: 0,
    offset: pagination.offset,
    limit: pagination.limit,
  };

  return {
    banners,
    paginationData,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook lấy chi tiết một banner
 */
export function useBannerDetail(id) {
  const { data, loading, error, refetch } = useQuery(GET_BANNER_DETAIL, {
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const banner = data?.banner?.getBannerDetail || null;

  return {
    banner,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook lấy hàng chờ (queue) của một màn hình
 */
export function useBannerQueue(input) {
  const shouldSkip = !input?.type || !input?.screen;

  const { data, loading, error, refetch } = useQuery(GET_BANNER_QUEUE, {
    variables: { input },
    skip: shouldSkip,
    fetchPolicy: "cache-and-network",
  });

  const queueItems = data?.banner?.getBannerQueue || [];

  return {
    queueItems,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook tạo mới banner
 */
export function useCreateBanner() {
  const [mutate, { loading }] = useMutation(CREATE_BANNER, {
    refetchQueries: ["SearchBanners"],
  });

  const createBanner = useCallback(
    async (input) => {
      try {
        const response = await mutate({ variables: { input } });
        notify.success({ message: "Tạo công cụ truyền thông thành công" });
        return response?.data?.banner?.createBanner;
      } catch (err) {
        notify.error({ message: err?.message || "Tạo công cụ truyền thông thất bại" });
        throw err;
      }
    },
    [mutate]
  );

  return { createBanner, loading };
}

/**
 * Hook cập nhật banner
 */
export function useUpdateBanner() {
  const [mutate, { loading }] = useMutation(UPDATE_BANNER, {
    refetchQueries: ["SearchBanners", "GetBannerDetail"],
  });

  const updateBanner = useCallback(
    async (id, input) => {
      try {
        const response = await mutate({ variables: { id, input } });
        notify.success({ message: "Cập nhật công cụ truyền thông thành công" });
        return response?.data?.banner?.updateBanner;
      } catch (err) {
        notify.error({ message: err?.message || "Cập nhật công cụ truyền thông thất bại" });
        throw err;
      }
    },
    [mutate]
  );

  return { updateBanner, loading };
}

/**
 * Hook bật / tắt banner (danh sách)
 */
export function useSwitchBannerStatus() {
  const [mutate, { loading }] = useMutation(SWITCH_BANNER_STATUS, {
    refetchQueries: ["SearchBanners"],
  });

  const switchStatus = useCallback(
    async (id, isActive) => {
      try {
        const response = await mutate({
          variables: { id, isActive },
          update(cache, { data }) {
            const updated = data?.banner?.switchBannerStatus;
            if (updated) {
              cache.modify({
                id: cache.identify({ __typename: "Banner", id }),
                fields: {
                  isActive() {
                    return updated.isActive;
                  },
                },
              });
            }
          },
        });
        notify.success({
          message: isActive ? "Đã bật công cụ truyền thông" : "Đã tắt công cụ truyền thông",
        });
        return response?.data?.banner?.switchBannerStatus;
      } catch (err) {
        notify.error({ message: err?.message || "Không thể thay đổi trạng thái" });
        throw err;
      }
    },
    [mutate]
  );

  return { switchStatus, loading };
}

/**
 * Hook lưu thứ tự hàng chờ
 */
export function useSaveBannerQueue() {
  const [mutate, { loading }] = useMutation(SAVE_BANNER_QUEUE, {
    refetchQueries: ["GetBannerQueue", "SearchBanners"],
  });

  const saveQueue = useCallback(
    async (input) => {
      try {
        const response = await mutate({ variables: { input } });
        notify.success({ message: "Lưu cấu hình thứ tự thành công" });
        return response?.data?.banner?.saveBannerQueue;
      } catch (err) {
        notify.error({
          message: err?.message || "Lưu thứ tự hàng chờ thất bại",
        });
        throw err;
      }
    },
    [mutate]
  );

  return { saveQueue, loading };
}
